import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Home, Search, TrendingUp, Shield, Clock, DollarSign, MapPin } from "lucide-react";
// Contest disabled until site has traffic
// import ContestBanner from "@/components/ContestBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = ({ user }) => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-slate-50">
      <Helmet>
        <title>Its-Sold.com™ - FREE FSBO Listings in Dallas-Fort Worth | Sell Your Home Without Agent Fees</title>
        <meta name="description" content="List your home for FREE on DFW's premier For Sale By Owner platform. Save $25K+ in commissions. No agent fees, ever. Serving Dallas, Fort Worth, Plano, Frisco & all DFW cities." />
        <link rel="canonical" href="https://its-sold.com/" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-orange-100 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <Home className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></h1>
                <p className="text-xs text-gray-600">DFW&apos;s Free FSBO Platform</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/search')}
                className="text-gray-700 hover:text-orange-600"
                data-testid="nav-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Browse Listings
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/resources')}
                className="text-gray-700 hover:text-orange-600"
                data-testid="nav-resources-btn"
              >
                Resources
              </Button>
              {user ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  data-testid="nav-dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <Button
                  onClick={handleLogin}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sticky Banner disabled - re-enable when site has traffic
      <div className="pt-20">
        <ContestBanner variant="sticky" />
      </div>
      */}
      <div className="pt-20"></div>

      {/* Hero Section */}
      <div className="pt-12 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center fade-in">
          {/* Contest Hero Banner disabled
          <ContestBanner variant="hero" />
          */}
          
          <div className="inline-block mb-6">
            <span className="bg-orange-100 text-orange-700 px-5 py-2 rounded-full text-sm font-medium border border-orange-200">
              🏆 DFW's #1 Free FSBO Platform
            </span>
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'Playfair Display' }}>
            Sell Your Home.
            <br />
            <span className="text-orange-600">Keep Your Commission.</span>
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            List your Dallas/Fort Worth home for <span className="font-semibold text-orange-600">100% FREE</span>. Get AI-powered pricing, professional exposure, and direct buyer connections. Save thousands in agent fees.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => user ? navigate('/create') : handleLogin()}
              className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-10 py-6 rounded-xl shadow-xl hover:shadow-2xl transition-all"
              data-testid="hero-list-free-btn"
            >
              <Home className="w-5 h-5 mr-2" />
              List Your Home - Free
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/search')}
              className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 text-lg px-10 py-6 rounded-xl"
              data-testid="hero-browse-btn"
            >
              <Search className="w-5 h-5 mr-2" />
              Browse Listings
            </Button>
          </div>
          <div className="mt-8 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-orange-600" style={{ fontFamily: 'Playfair Display' }}>$0</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Listing Fee</div>
            </div>
            <div className="text-center border-x border-orange-200">
              <div className="text-2xl sm:text-4xl font-bold text-orange-600" style={{ fontFamily: 'Playfair Display' }}>50+</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Free Photos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-orange-600" style={{ fontFamily: 'Playfair Display' }}>AI</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Smart Tools</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Choose Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup>?
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to sell your home successfully, without the 5-6% commission
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 hover-lift">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <DollarSign className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>100% Free Forever</h4>
              <p className="text-gray-600 leading-relaxed">
                No hidden fees, no credit card required. List unlimited properties and keep every penny from your sale.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover-lift">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>AI-Powered Tools</h4>
              <p className="text-gray-600 leading-relaxed">
                GPT-5.1 descriptions, Gemini pricing analysis, and Claude educational guides. Sell smarter, not harder.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-green-50 to-white border border-green-100 hover-lift">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>DFW Focused</h4>
              <p className="text-gray-600 leading-relaxed">
                Built specifically for the Dallas/Fort Worth Metroplex. Search by all 13 counties and local ZIP codes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
              List Your Home in Minutes
            </h3>
            <p className="text-lg text-gray-600">Simple, fast, and completely free</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Sign Up Free", desc: "Quick Google login - no passwords needed" },
              { step: "2", title: "Add Details", desc: "Upload photos, describe your home, AI does the rest" },
              { step: "3", title: "Go Live", desc: "Your listing is instantly searchable across DFW" },
              { step: "4", title: "Connect & Sell", desc: "Get direct buyer inquiries and close the deal" }
            ].map((item, idx) => (
              <div key={idx} className="text-center fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  {item.step}
                </div>
                <h5 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>{item.title}</h5>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Save Thousands?
          </h3>
          <p className="text-xl mb-10 text-orange-100">
            Join DFW sellers who kept their commission. Start listing today - 100% free, forever.
          </p>
          <Button
            size="lg"
            onClick={() => user ? navigate('/create') : handleLogin()}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-12 py-6 rounded-xl shadow-2xl"
            data-testid="cta-get-started-btn"
          >
            Get Started Now - It's Free
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Landing;